import React, { forwardRef } from "react";

import {
  Box,
  styled,
  FilledInput as MuiFilledInput,
  FilledInputProps as MuiFilledInputProps,
  Typography,
  useTheme,
} from "@mui/material";

import { FieldLabel } from "../FieldLabel";
import {
  fieldAccentColor,
  fieldBorderColor,
  fieldErrorColor,
  fieldErrorRing,
  fieldFill,
  FIELD_FONT_SIZE,
  FIELD_PLACEHOLDER_OPACITY,
  fieldFocusRing,
  fieldHoverRing,
} from "../../../theme/fieldChrome";

// Shared input focus treatment (design-system standard, mode-aware):
// neutral 1px border at rest -> on focus the border tints to brand + a soft
// low-opacity brand "ring" (box-shadow). Border width never changes (no layout
// shift), and a hard loud purple border is avoided in favour of the soft ring.
const StyledMuiFilledInput = styled(MuiFilledInput)<MuiFilledInputProps>(
  ({ theme, disabled, readOnly }) => ({
    borderRadius: 8,
    // Border, fill and focus all come from `fieldChrome` — the single source every field
    // and selector reads, so a text field and a select beside it cannot end up on two
    // different greys (which is exactly what happened: grey[200] here against primary[100]
    // on SelectField, side by side in one form).
    border: `1px solid ${disabled ? fieldBorderColor(theme) : fieldBorderColor(theme)}`,
    transition: `border-color 0.2s ease, box-shadow 0.2s ease`,
    // WHITE in light mode. I had this as the page tone for one revision, reasoning that a
    // field should be a recess below its panel. That is true INSIDE a panel and false
    // everywhere else: a search box or filter sits directly on the tinted page, where a
    // page-toned field is invisible. A field has to work on both surfaces, and only white
    // does — on the page it is the lighter, raised thing; inside a white panel its border
    // defines it, at 1.53:1, which is what every comparable product relies on.
    //
    // It also keeps controls consistent: secondary buttons are already white here, so a
    // button and a field beside it now read as the same kind of object.
    backgroundColor: fieldFill(theme),
    // On the ROOT, deliberately — see `FIELD_FONT_SIZE`. MUI's `line-height: 1.4375em` is
    // declared here and inherits down as a computed length, so a field that patches only its
    // inner input keeps body1's 20px line box.
    fontSize: FIELD_FONT_SIZE,
    // Leading icon, RESTING state. Dim, so "active" has somewhere to go — at rest the
    // whole leading edge of an untouched field reads as one quiet unit rather than a
    // bright glyph beside grey placeholder text. The focus counterpart lives in the
    // `.Mui-focused` block below; both are set HERE rather than on the Box's own `sx`,
    // since an sx colour is emitted later and would beat the focus rule.
    "& [data-input-adornment='start']": {
      color: theme.palette.text.secondary,
      transition: "color 0.15s ease",
    },
    /*
      Placeholder contrast — the one number behind "the text goes bold when you type".

      Measured before this: placeholder and value are the SAME colour
      (rgb(250,250,250) in dark) at the SAME weight (400); the placeholder simply
      renders at MUI's default 0.5 opacity and the value at 1.0. Nothing bolds. Identical
      glyphs at double the contrast read as heavier, which is what people describe as
      bold, and no amount of weight-fiddling touches it because no weight changes.

      0.65 halves the perceived jump (0.5→1.0 becomes 0.65→1.0) while keeping the
      placeholder clearly subordinate to a real value — the distinction a placeholder
      exists to make. It cannot be removed entirely: a legible value and a
      distinguishable placeholder REQUIRE a contrast difference, so this is a tuning
      knob, not a fix.

      Set here rather than per-field so every `@repo/ui` Input shares it — the
      alternative is each surface picking its own placeholder weight, which is the
      drift §31 exists to prevent. The handful of call sites that already override
      `::placeholder` locally (SignInPage, PasswordSetup, the Stripe card fields) keep
      winning on specificity, deliberately: the Stripe ones must match an iframe they
      do not control.

      The number moved to `fieldChrome` because "every `@repo/ui` Input" turned out not to
      cover every FIELD: the shared `Autocomplete` is not built on this component and had no
      such rule, so it rendered its placeholder at MUI's default beside three fields using
      this one. A value that two components must agree on does not live in one of them.
    */
    "& .MuiInputBase-input::placeholder": {
      opacity: FIELD_PLACEHOLDER_OPACITY,
    },
    // The root owns the inset for BOTH spellings now (see the two notes below). Vertical
    // 12px matches what the single-line slot used to supply, so field heights are unchanged.
    padding: 12,
    // SINGLE-LINE inset. Target MUI's own class rather than a bare `input` element
    // selector so it survives MUI changing the element it renders. Note this class is
    // on the input SLOT, so it matches the multiline <textarea> too — which is why the
    // multiline block below has to undo it. Keep the two in sync.
    // …and it was ALSO stacking on the single-line field, which nobody caught when the
    // multiline half was fixed. MUI's FilledInput root already carries `0 12px`, so a
    // further 12px on the input slot put single-line text 24px in from the border while a
    // textarea sat at 12px. Measured, not eyeballed: 24 vs 12.
    //
    // Fixed the same way as multiline — pad the ROOT, zero the slot — so both spellings
    // now use one mechanism and land on the same 12px. Padding the root (not the slot) is
    // also what keeps a leading icon and the text it precedes on the same inset.
    "& .MuiFilledInput-input": {
      padding: 0,
      // AUTOFILL lives here, on the input SLOT, because that is the element Chrome
      // marks with `:-webkit-autofill` — the root <div> never matches it. Declared
      // inside this block rather than as a second `& .MuiFilledInput-input` key: a
      // duplicate key in the same object literal silently drops the `padding: 0`
      // above, taking the single-line inset with it.
      //
      // A field Chrome has filled must look exactly like one the user typed into.
      // TWO different things break that and only one of them is the browser:
      //
      //  1. DARK MODE — MUI itself. `FilledInput.js` (~L146; `OutlinedInput.js` ~L95
      //     is the same) hardcodes `WebkitBoxShadow: '0 0 0 100px #266798 inset'`
      //     plus white text on `:-webkit-autofill` whenever `palette.mode === 'dark'`.
      //     #266798 is a steel blue belonging to no palette here. This is the loud
      //     one, and it is our DEPENDENCY's default, not Chrome's.
      //  2. LIGHT MODE — Chrome. MUI sets all three properties to `null` in light, so
      //     the UA styling (pale #e8f0fe fill, dark blue text) shows through untouched.
      //
      // It has to be an INSET BOX-SHADOW, not `background-color`: Chrome paints the
      // autofill fill with `!important` in the UA origin, so a background declaration
      // is ignored while a box-shadow paints over it — and a box-shadow is also what
      // (1) set, so nothing weaker displaces it. `-webkit-text-fill-color` is likewise
      // the only lever on the glyph colour; plain `color` loses to the UA rule.
      //
      // The long-`transition` trick must NOT be used on its own — it is what was tried
      // before and it cannot work here: it has no effect on (1) whatsoever, and it
      // fails on a field Chrome fills at PAGE LOAD (no prior value to transition from),
      // which is exactly the sign-in case. It is kept below only as belt-and-braces.
      //
      // One declaration covers both modes on purpose: `fieldFill` and `text.primary`
      // are already mode-aware, and reading them here is what stops the covering fill
      // drifting from the field it covers.
      //
      // Specificity: `.css-root .MuiFilledInput-input:-webkit-autofill` is (0,3,0)
      // against MUI's (0,2,0) on the slot, so this wins whatever order emotion emits.
      //
      // NOTE: the fill is read from the THEME. A call site that overrides the field's
      // background through `sx` is NOT covered and has to declare its own autofill
      // fill alongside it — the platform sign-in page is that case.
      "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
        {
          WebkitBoxShadow: `0 0 0 1000px ${fieldFill(theme)} inset`,
          WebkitTextFillColor: theme.palette.text.primary,
          caretColor: theme.palette.text.primary,
          transition: "background-color 9999s ease-in-out 0s",
        },
    },
    // MULTILINE is a <textarea>, and MUI already solves it correctly: for multiline it
    // pads the ROOT (`25px 12px 8px` — room for a floating label this component does not
    // use) and zeroes the input slot's padding on all four sides. See FilledInput.js,
    // root styles ~L120 and input styles ~L178 in @mui/material 5.18.
    //
    // The bug was that the single-line rule ABOVE also matches the textarea: MUI puts
    // `.MuiFilledInput-input` on the input slot whichever element it renders as. So it
    // re-added a 12px inset to the textarea, which stacked on the root's 12px below —
    // 24px above the first line and 24px below it. Symmetric padding in a `minRows`-tall
    // box is precisely what "the placeholder is vertically centred" looks like. It was
    // never an alignment problem, so there is no `alignItems` here to fix.
    //
    // Restore MUI's split (pad the root, zero the input), with enough specificity —
    // root class + descendant class (0,3,0) — to beat the single-line rule (0,2,0)
    // regardless of the order emotion emits them in.
    //
    // The previous attempt at this used a bare `textarea` selector (0,1,1), which loses
    // to `.MuiFilledInput-input` (0,2,0) on specificity and so never applied at all.
    "&.MuiInputBase-multiline": {
      padding: 12,
      "& .MuiFilledInput-input": {
        padding: 0,
      },
    },
    // AUTOFILL. A field that Chrome has filled must look exactly like a field the
    // user typed into — same fill, same text colour. Two DIFFERENT things break that,
    // and only one of them is Chrome:
    //
    //  1. DARK MODE — MUI itself. `FilledInput.js` (~L146, and `OutlinedInput.js` ~L95)
    //     hardcodes `WebkitBoxShadow: '0 0 0 100px #266798 inset'` + white text on
    //     `:-webkit-autofill` whenever `palette.mode === 'dark'`. #266798 is a steel
    //     blue that belongs to no palette here. This is the loud one, and it is OUR
    //     dependency's default, not the browser's.
    //  2. LIGHT MODE — Chrome. MUI sets all three properties to `null` in light, so
    //     the UA styling (pale blue #e8f0fe fill, dark blue text) shows through
    //     untouched.
    //
    // The fix has to be an INSET BOX-SHADOW, not `background-color`: Chrome paints the
    // autofill fill with `!important` in the UA origin, so a background declaration is
    // ignored, while a box-shadow paints OVER it. It also has to be a box-shadow to beat
    // (1), since a box-shadow is what MUI set. `-webkit-text-fill-color` is likewise the
    // only way to move the glyph colour — plain `color` loses to the UA rule.
    //
    // Colours come from the same helpers the resting field uses, so this cannot drift
    // from the field it is covering. Both modes are handled by one declaration because
    // `fieldFill` / `text.primary` are already mode-aware — there is no dark branch here
    // on purpose.
    //
    // NOTE: this reads the fill from the THEME. A call site that overrides the field's
    // background via `sx` (the sign-in page does) is not covered and must state its own
    // autofill fill alongside it.
    //
    // Specificity: `.css-root .MuiFilledInput-input:-webkit-autofill` is (0,3,0) against
    // MUI's own (0,2,0) on the input slot, so this wins regardless of emission order.
    // The `transition` is belt-and-braces for Chrome's background paint; the box-shadow
    // is what actually does the work. The long-transition trick ALONE is not sufficient
    // and should not be used on its own: it has no effect on (1) at all, and it fails on
    // a field Chrome fills at page load, which is precisely the sign-in case.
    // HOVER — brand border + a SOFT glow, both skipped for `disabled` and `readOnly`
    // (neither can be typed into, so neither should advertise that it can). Border
    // and ring both come from `fieldChrome` now rather than the inline
    // `theme.palette.brand?.[600] || "#7F48DF"` duplication this used to carry —
    // one fewer place for the brand fallback hex to drift from `fieldAccentColor`'s.
    ":hover": {
      backgroundColor: fieldFill(theme),
      borderColor:
        disabled || readOnly ? fieldBorderColor(theme) : fieldAccentColor(theme),
      boxShadow: disabled || readOnly ? "none" : fieldHoverRing(theme),
    },
    // The FilledInput root is a <div> — a bare `:focus` never matches (only the
    // inner <input>/<textarea> is focusable). Target MUI's `.Mui-focused` class
    // (added to the root on focus) plus `:focus-within` as a fallback. Soft brand
    // ring (matches @repo/ui TextField) — brand border + low-opacity glow, no
    // width change. `brand` is the purple scale; `primary` is neutral grey.
    "&.Mui-focused, &:focus-within": {
      // Re-assert the fill, exactly as `:hover` above does, and for the same reason.
      //
      // MUI's FilledInput ships its own background per state — `rgba(0,0,0,0.06)` at
      // rest and on focus in light, `rgba(0,0,0,0.09)` on hover — at
      // `.MuiFilledInput-root.Mui-focused`, i.e. (0,2,0). The root's `fieldFill` on
      // line ~43 is only (0,1,0), so it loses here and the field reverted to MUI's
      // BLACK ALPHA the moment it was focused.
      //
      // That is why it read as "too dark after you click on it and move the mouse
      // away": hover already re-asserted the fill (the block above), so the state
      // that showed the regression was focused-but-not-hovered. Measured on the
      // signup form in light mode — rest `rgb(250,250,250)`, focused
      // `rgba(0, 0, 0, 0.06)` composited over the page.
      //
      // A black alpha is also wrong in principle, not just too dark: it tints
      // whatever is behind it, so the field's colour depends on the surface it
      // happens to sit on. `fieldFill` is opaque, so a field is the same colour on a
      // card, a drawer and the ambient auth page.
      //
      // Emission order is not being relied on here: this block's `borderColor` and
      // `boxShadow` already beat MUI in the same selector, which is what makes the
      // focus ring visible at all, so the background is carried by the identical
      // specificity that the ring already proves wins.
      //
      // Border and ring come from `fieldChrome` rather than the inline
      // `brand?.[600] || "#7F48DF"` this carried on main. Same computed values —
      // `fieldAccentColor` IS that expression and `fieldFocusRing` is the same
      // 3px/`600-6%` ring — but the brand fallback hex now lives in one place
      // instead of being repeated per state (§31: field borders inherit from one
      // @repo/ui source). This line was a merge conflict; the fill re-assertion
      // above is main's fix and the helpers are this branch's refactor, and they
      // are orthogonal, so both survive.
      backgroundColor: fieldFill(theme),
      borderColor: fieldAccentColor(theme),
      boxShadow: fieldFocusRing(theme),
      // The leading icon goes to FULL contrast when the field is active.
      //
      // This is the honest half of "the search text bolds on focus". Nothing in this
      // repo changes `fontWeight` on focus or hover — there is no such rule in
      // `packages/ui` or `apps/platform`, and Lato 300–700 are all loaded, so it is
      // not synthetic bolding either. What actually changes is CONTRAST: the
      // placeholder renders dimmed and the typed value renders at full strength, and
      // grey glyphs read lighter than the same glyphs at full contrast. So the fix
      // for "it looks like it thickens" is to make the contrast change deliberate and
      // to put it on the icon, which is decoration, rather than leaving it to fall
      // out of placeholder-vs-value on the text.
      //
      // Only the icon. Vergence also turns the typed text brand-purple; not adopted,
      // because purple is this app's ACTION colour — the ::selection highlight, focus
      // rings, primary fills and selected rows are all brand — and a search query is
      // the user's own content, not an action. It would also make this the only field
      // in the platform whose VALUE renders in a different colour from every other
      // field's, which is the drift §31 exists to prevent.
      "& [data-input-adornment='start']": {
        color: theme.palette.text.primary,
      },
    },
    /*
      INVALID. The error colour substitutes for the brand accent in all three states a
      valid field would tint — rest, hover, focus — and focus still gets its soft ring,
      in red rather than brand.

      This block did not exist, and that absence WAS the bug. `Input`'s `error` prop went
      only to MUI (which adds the `.Mui-error` class and nothing else here) and to this
      component's own `helperText`, which the platform `TextField` never passes — it
      renders its own `FormHelperText` below instead. So an errored field's border
      computed to rgb(39,39,42), the rest colour, and red helper text was the entire
      error signal. Both apps were affected: platform's `TextField`/`DatePickerField`
      wrappers and `apps/app`'s `FieldText` all route `error` into a field component.

      HOVER AND FOCUS ARE BOTH RESTATED HERE ON PURPOSE. Left to fall through, an errored
      field would go brand-purple the instant the pointer or the caret arrived — the app's
      "this is the active, healthy thing" colour, shown at precisely the moment the user
      is trying to correct it. The state a field is IN outranks the state it is being
      touched in.

      Specificity, since these deliberately fight the rules above rather than relying on
      emission order: `.root.Mui-error` (0,2,0) beats `.root:hover` (0,1,1);
      `.root.Mui-error.Mui-focused` (0,3,0) and `.root.Mui-error:focus-within` (0,2,1)
      both beat `.root.Mui-focused` (0,2,0). So this holds whichever order emotion emits.
    */
    "&.Mui-error": {
      borderColor: fieldErrorColor(theme),
      "&:hover": {
        borderColor: fieldErrorColor(theme),
      },
      "&.Mui-focused, &:focus-within": {
        borderColor: fieldErrorColor(theme),
        boxShadow: fieldErrorRing(theme),
      },
    },
    opacity: disabled ? 0.4 : 1,
  })
);

export interface InputProps {
  label?: string;
  /**
   * Field name, forwarded to the DOM input.
   *
   * Load-bearing for react-hook-form: RHF resolves which field changed from
   * `event.target.name` and silently drops the update when it can't (its change
   * handler does the value write inside `if (field)`). Without a name on the
   * element, a form could look filled in and still fail validation as empty —
   * which is exactly what the sign-up form did.
   */
  name?: string;
  required?: boolean;
  placeholder?: any;
  defaultValue?: any;
  type?: string;
  helperText?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  labelAdornment?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  // Non-editable display variant — keeps full contrast + the field box (unlike
  // `disabled`, which fades to 0.4 opacity), for informational fields like Role.
  readOnly?: boolean;
  minRows?: number;
  maxRows?: number;
  multiline?: boolean;
  sx?: object;
  inputProps?: MuiFilledInputProps["inputProps"];
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Ref to the inner <input>/<textarea>, NOT the wrapper. `ref` on a MUI
   * InputBase lands on the root <div>, which is the wrong element for anything
   * that needs to read `.value` — react-hook-form being the case that matters:
   * it reads the field's current value straight off the node it was given.
   */
  inputRef?: React.Ref<HTMLInputElement>;
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
  value?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * The missing half of `onBlur`, which this component has always forwarded.
   *
   * A field that renders differently at rest than under the cursor — a money field
   * grouping `2500000` as `2,500,000` while blurred, say — needs BOTH edges, and its
   * absence pushed the one caller that wanted it toward `inputProps.onFocus`, i.e. two
   * spellings of the same event on one component depending on which edge you needed.
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelAdornment,
      name,
      required = false,
      fullWidth = true,
      placeholder,
      startAdornment,
      endAdornment,
      error,
      helperText,
      disabled = false,
      readOnly = false,
      defaultValue = "",
      type = "text",
      minRows = 2,
      maxRows = 10,
      multiline = false,
      sx,
      inputProps,
      inputRef,
      onChange = () => {},
      onValueChange = () => {},
      handleKeyDown,
      value,
      onBlur,
      onFocus,
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const theme = useTheme();

    // Read-only fields keep their box + full contrast but are non-editable and
    // do not adopt the focus (brand) border, so they don't read as editable.
    const readOnlySx = readOnly
      ? {
          cursor: "default",
          "& input": { cursor: "default" },
          "&.Mui-focused, &:focus-within": {
            border: `1px solid ${theme.palette.grey?.[200] || "#d1d3d6"}`,
          },
        }
      : {};

    const onWheel = (e: any) => {
      // prevent default scroll behaviour when scrolling inside number input
      if (type === "number") e.target instanceof HTMLElement && e.target.blur();
    };

    const onKeyDown = (e: any) => {
      if (type === "number") {
        if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
          e.preventDefault();
        }
      }
    };

    // ORDER IS LOAD-BEARING: commit the value FIRST, then notify.
    //
    // `onChange` is what writes the value into the form library (react-hook-form,
    // via the platform TextField wrapper). `onValueChange` is the side-effect hook
    // callers use to REACT to the new value. Firing the side effect first meant
    // every caller observed the value as it was BEFORE this keystroke.
    //
    // Concretely, the bug this caused on the signup form: the page re-validates a
    // field on change once it has already errored. With the old order, that
    // re-validation ran against the previous value — so the very keystroke that
    // FIXED the field validated the state before it, the error survived, and if the
    // user then stopped typing nothing ever re-checked it. The error stayed on
    // screen under a visibly correct field, permanently. Anything that delivers the
    // whole correction in ONE change event — paste, browser autofill, a password
    // manager — hit it every single time, because there was never a "next
    // keystroke" to catch up.
    //
    // A callback named `onValueChange` that fires before the value is committed is
    // wrong for every consumer, not just that form, which is why this is fixed here
    // rather than worked around in the caller.
    const handleOnChange = (e: any) => {
      onChange(e.target.value);
      if (onValueChange) {
        onValueChange(e.target.value);
      }
    };

    // ONE element, not a fragment.
    //
    // As a fragment, a field's label and its control were separate children of whatever
    // laid the form out — so a parent `<Stack spacing={n}>` injected ITS gap between the
    // label and the control, and FieldLabel's canonical 8px stopped applying (measured:
    // margin-bottom computed to 0 and the gap rendered at the Stack's 20px). Every field
    // inside a Stack was affected, and the gap silently varied with each form's spacing.
    //
    // Wrapping makes the field a single layout child: the parent's spacing separates
    // FIELDS, and the label-to-control distance is FieldLabel's alone (§31).
    return (
      <Box>
        <FieldLabel label={label} adornment={labelAdornment} />
        <StyledMuiFilledInput
          ref={ref}
          inputRef={inputRef}
          name={name}
          type={type}
          required={required}
          fullWidth={fullWidth}
          disableUnderline
          placeholder={placeholder}
          multiline={multiline}
          minRows={minRows}
          maxRows={maxRows}
          /**
           * Leading adornments get a gap; MUI does not add one for a bare node.
           *
           * `InputAdornment` carries its own margin, but every one of the 11 call
           * sites in this repo passes a raw `<SearchIcon/>` instead (checked — none
           * passes `<InputAdornment>`, so this cannot double-space anything). The
           * result was `input.left === icon.right` on EVERY search field with a
           * leading icon: measured 0px between the magnifier and the placeholder in
           * the ⌘K palette, the tours dialog and the page toolbars alike, which is
           * what makes them read as cramped next to the Filters fields — those put
           * their magnifier on the trailing edge and so never showed it.
           *
           * Fixed here rather than at the call sites because there are eleven of
           * them and they would drift. 8px on the 4px base, matching FieldLabel's
           * label→control gap so the horizontal and vertical rhythms agree.
           */
          startAdornment={
            startAdornment ? (
              <Box
                // Targeted by the root's `.Mui-focused` rule above to brighten on focus.
                // A data attribute rather than a class so the selector does not depend on
                // emotion's generated names, and rather than an `sx` conditional because
                // this component does not track focus in React state — the browser does,
                // and CSS can already read it.
                data-input-adornment="start"
                // NO `color` here on purpose. Both the rest and focus colours are set by
                // the styled root (see the `[data-input-adornment='start']` rules), because
                // an `sx` colour on this Box is emitted AFTER the root's descendant rule and
                // wins — which is exactly how the first version of this failed: the icon
                // dimmed correctly at rest and then never brightened, while the field's brand
                // focus ring proved it really was focused. Keeping both states in one origin
                // is what lets the focus rule out-specify the resting one.
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mr: 2 }}
              >
                {startAdornment}
              </Box>
            ) : undefined
          }
          endAdornment={endAdornment}
          sx={{ ...readOnlySx, ...(sx || {}) }}
          disabled={disabled}
          error={error}
          // Also passed at the top level (not just inside `inputProps` below) so the
          // STYLED component's own style callback can read it as a plain JS boolean —
          // the same mechanism `disabled` already uses to skip the hover treatment.
          // `inputProps.readOnly` below still wins for what actually reaches the DOM
          // `<input readonly>` attribute; this copy exists purely for the style rules.
          readOnly={readOnly}
          inputProps={{ ...inputProps, readOnly }}
          onWheel={onWheel}
          onKeyDown={onKeyDown}
          onChange={handleOnChange}
          onBlur={onBlur}
          onFocus={onFocus}
          {...(isControlled ? { value } : { defaultValue })}
        />
        {helperText && (
          <Typography
            variant="body3"
            // No `ml`. It used to carry `ml: 1` (4px), which indented the helper text
            // relative to BOTH the label above it and the control it describes — and it
            // reads as a broken hanging indent the moment the text wraps, because the
            // block edge sits 4px right of everything else on the field. A field's
            // label, control and helper text share one left edge (§31); nothing gets a
            // decorative offset of its own.
            // 12px. It went 4 → 8 (matching FieldLabel's label→control gap) and 8 still read
            // as stuck to the field's underside, so this is set by eye against a TextArea
            // rather than by symmetry with the label.
            //
            // The asymmetry is deliberate and it is not arbitrary: the label sits above a
            // control it names, where tightness reads as belonging. The helper sits below a
            // control it comments on, and on a tall TextArea there is a large empty box
            // immediately above it — the same CSS gap reads smaller against that mass than
            // it does under a 49px field. §40's warning applies: the gap you perceive is not
            // the margin you set, so this one is measured in the browser, not derived.
            // `display: block` is load-bearing, not tidiness. Typography renders body3 as a
            // <span>, and margin-top DOES NOT APPLY to an inline box — so this margin was
            // silently ignored at every value it was ever set to, and the ~3px under the
            // control was line-height leading, not a gap anyone chose. Measured in the
            // browser: marginTop computed to 12px while the span's top sat 3px below the
            // input's bottom. Three attempts at tuning the number could not have worked.
            // ABSOLUTE px, not `mt: 3` (working-conventions §47). This is a layer-1
            // component and the two apps have different spacing bases -- @repo/ui 4px,
            // apps/app MUI's default 8px -- so the multiplier that renders the 12px
            // tuned above rendered 24px in apps/app, where the error sat further from
            // the field than its own label sat above it. Measured, not inferred.
            sx={{ display: 'block', mt: '12px' }}
            color={error ? "error.main" : "text.secondary"}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }
);
